<template>
  <input
    :class="{ 'hide': hide }"
    type="file"
    ref="file"
    :accept="acceptAttribute"
    @change="handleChange($event.target.files[0])"
  >
</template>

<script>
import { mapActions } from 'vuex'
import ExcelJS from 'exceljs'
import { formatNumber } from 'utils/functions'
import { isValidArray } from 'corp-front-vue-commons/src/utils/functions'

export default {
  name: 'XLSXInput',
  props: {
    worksheetTarget: {
      type: [String, Array],
      required: true
    },
    maxLines: {
      type: Number,
      default: 2000
    },
    supportedFileTypes: {
      type: Array,
      default: () => [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ]
    },
    hide: {
      type: Boolean,
      default: false
    },
    validator: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    acceptAttribute () {
      return this.supportedFileTypes.join(', ')
    }
  },
  data () {
    return {
      namespace: 'Components.XLSXInput'
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isValidArray(val)
    },

    clearInput () {
      const $input = this.$refs.file
      if ($input) $input.value = ''
    },

    async xlsxLinesCount (file) {
      const workbook = new ExcelJS.Workbook()
      const worksheet = await workbook.xlsx.load(file)
      const worksheetTarget = this.worksheetTarget
      let lines = []

      worksheet.eachSheet(row => {
        if (row.name.toLowerCase() === worksheetTarget.toLowerCase()) {
          lines = row._rows.filter((r, i) => i > 2 && r.values.length)
        }
      })

      return lines.length
    },

    async xlsxMultipleTabsLinesCount (file) {
      const workbook = new ExcelJS.Workbook()
      const data = await workbook.xlsx.load(file)
      const worksheets = data._worksheets.filter(row => Boolean(row))
      const tabs = worksheets.map(row => {
        return this.worksheetTarget.reduce((acc, item, index) => {
          if (row.name.toLowerCase() === item.toLowerCase()) {
            const length = row._rows.filter((r, i) => i > 0 && r.values.length).length
            acc.tabName = row.name
            acc.count = length
          }
          return acc
        }, {})
      })

      const arr = tabs.map(tab => tab.count).sort((a, b) => a < b ? 1 : -1)
      return this.isValidArray(arr) ? arr[0] : 0
    },

    async xlsxValidator (file) {
      const xlsxLinesCount = this.isValidArray(this.worksheetTarget)
        ? await this.xlsxMultipleTabsLinesCount(file)
        : await this.xlsxLinesCount(file)

      const fileType = file.type
      const isValidFileType = this.supportedFileTypes.some(type => type === fileType)

      if (!isValidFileType) {
        this.validator && this.showAlert({
          type: 'info',
          message: this.$t(`${this.namespace}.invalidFileType`)
        })
        this.$emit('error', 'invalidFileType')
        return false
      }

      if (!xlsxLinesCount) {
        this.validator && this.showAlert({
          type: 'info',
          message: this.$t(`${this.namespace}.emptyFile`)
        })
        this.$emit('error', 'emptyFile')
        return false
      }

      if (xlsxLinesCount > this.maxLines) {
        this.validator && this.showAlert({
          type: 'info',
          message: this.$t(`${this.namespace}.maxLinesError`)
            .replace(/\[MAX_LINE\]/g, formatNumber(this.maxLines))
        })
        this.$emit('error', 'maxLinesError')
        return false
      }

      return true
    },

    async handleChange (file) {
      const isValidFile = await this.xlsxValidator(file)
      this.clearInput()

      if (isValidFile) {
        this.$emit('change')
        this.$emit('success', file)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.hide {
  bottom: 0;
  left: 0;
  opacity: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 1;
}
</style>
